import { useMutation, useQueryClient } from '@tanstack/react-query';
import { keyResultService } from '../../services/keyResultService';
import type { KeyResultCreate, KeyResultUpdate } from '../../models/keyResult.schemas';

/**
 * Hook for Key Result mutations (create, update, delete)
 * Provides cache invalidation and error handling
 *
 * @returns Mutation functions for key results
 */
export function useKeyResultMutations() {
  const queryClient = useQueryClient();

  /**
   * Create key result mutation
   * Validates business rules (max 5 KRs, objective status) in service layer
   */
  const createKeyResult = useMutation({
    mutationFn: (data: KeyResultCreate) => keyResultService.create(data),
    onSuccess: (newId, variables) => {
      // Invalidate key results queries for this objective
      queryClient.invalidateQueries({ queryKey: ['keyResults', variables.objectiveId] });
      
      // Invalidate objective query to refresh KR count
      queryClient.invalidateQueries({ queryKey: ['objective', variables.objectiveId] });
    },
    onError: (error) => {
      console.error('Error creating key result:', error);
    },
  });

  /**
   * Update key result mutation with optimistic updates
   */
  const updateKeyResult = useMutation({
    mutationFn: ({ id, data }: { id: string; data: KeyResultUpdate }) =>
      keyResultService.update(id, data),
    onMutate: async ({ id, data }) => {
      // Cancel outgoing queries
      await queryClient.cancelQueries({ queryKey: ['keyResults'] });

      // Get previous data for rollback
      const previousKeyResults = queryClient.getQueriesData({ queryKey: ['keyResults'] });

      // Optimistically update cache
      queryClient.setQueriesData({ queryKey: ['keyResults'] }, (old: any) => {
        if (!old || !Array.isArray(old)) return old;
        return old.map((kr: any) => (kr.id === id ? { ...kr, ...data } : kr));
      });

      return { previousKeyResults };
    },
    onError: (error, variables, context) => {
      console.error('Error updating key result:', error);

      // Rollback on error
      if (context?.previousKeyResults) {
        context.previousKeyResults.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSettled: (data, error, variables) => {
      // Invalidate key results queries to refetch
      queryClient.invalidateQueries({ queryKey: ['keyResults', variables.data.objectiveId] });
    },
  });

  /**
   * Delete key result mutation
   * Invalidates objective to recalculate progress
   */
  const deleteKeyResult = useMutation({
    mutationFn: (id: string) => keyResultService.delete(id),
    onMutate: async (id) => {
      // Cancel outgoing queries
      await queryClient.cancelQueries({ queryKey: ['keyResults'] });

      // Get previous data for rollback
      const previousKeyResults = queryClient.getQueriesData({ queryKey: ['keyResults'] });

      // Optimistically remove from cache
      queryClient.setQueriesData({ queryKey: ['keyResults'] }, (old: any) => {
        if (!old || !Array.isArray(old)) return old;
        return old.filter((kr: any) => kr.id !== id);
      });

      return { previousKeyResults };
    },
    onError: (error, variables, context) => {
      console.error('Error deleting key result:', error);

      // Rollback on error
      if (context?.previousKeyResults) {
        context.previousKeyResults.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSuccess: (data, keyResultId) => {
      // Invalidate all key results queries
      queryClient.invalidateQueries({ queryKey: ['keyResults'] });
      
      // Invalidate objective query to recalculate progress
      if (data?.objectiveId) {
        queryClient.invalidateQueries({ queryKey: ['objective', data.objectiveId] });
      }
    },
  });

  return {
    createKeyResult,
    updateKeyResult,
    deleteKeyResult,
  };
}
